// components/RelatedArticles.jsx
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const RelatedArticles = ({ currentArticleSlug }) => {
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/articles/related/${currentArticleSlug}`
        );

        if (!res.ok)
          throw new Error('No se pudieron cargar los artículos relacionados');

        const data = await res.json();
        setRelatedArticles(data);
      } catch (err) {
        console.error('Error loading related articles:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentArticleSlug) {
      fetchRelatedArticles();
    }
  }, [currentArticleSlug]);

  if (loading) {
    return (
      <section className="related-articles bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Artículos Relacionados
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse"
              >
                <div className="h-48 bg-gray-300"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="related-articles bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Artículos Relacionados
          </h2>
          <div className="text-center text-gray-600">
            <p>No se pudieron cargar los artículos relacionados</p>
          </div>
        </div>
      </section>
    );
  }

  if (relatedArticles.length === 0) {
    return (
      <section className="related-articles bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Artículos Relacionados
          </h2>
          <div className="text-center text-gray-600">
            <p>No hay artículos relacionados disponibles</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="related-articles bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Artículos Relacionados
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedArticles.map((article) => (
            <article
              key={article.id}
              className="relative bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <Link to={`/${article.slug}`}>
                <div className="relative overflow-hidden">
                  <img
                    src={article.featured_image_url}
                    alt={article.title}
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src =
                        'https://static.vecteezy.com/system/resources/previews/005/720/408/non_2x/crossed-image-icon-picture-not-available-delete-picture-symbol-free-vector.jpg';
                    }}
                  />
                </div>

                <div className="p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {article.categories
                      ?.flatMap((cat) =>
                        // Aplanar recursivamente y filtrar solo las que no tienen hijos
                        (function flattenLeaves(category) {
                          if (
                            category.children &&
                            category.children.length > 0
                          ) {
                            return category.children.flatMap(flattenLeaves);
                          }
                          return [category];
                        })(cat)
                      )
                      .map((category) => (
                        <span
                          key={category.id}
                          className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                        >
                          {category.name}
                        </span>
                      ))}
                  </div>
                  <h3 className="text-lg font-bold text-[#0076ba] hover:text-[#005a96] transition-colors">
                    {article.title}
                  </h3>

                  <div className="flex items-center justify-between text-sm text-gray-600 mt-3">
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {new Date(article.published_at).toLocaleDateString(
                        'es-MX',
                        {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }
                      )}
                    </span>
                  </div>

                  {article.author && (
                    <div className="text-xs text-gray-500 mt-2">
                      Por {article.author.display_name}
                    </div>
                  )}
                </div>
              </Link>
            </article>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#5cba3c] hover:bg-[#47bb21] transition-colors duration-200"
          >
            Ver todos los artículos
            <svg
              className="ml-2 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;
